import React from "react";
import { useNavigate } from "react-router-dom";
import Track from "../../components/track/track";

import { ModalContext } from "../../components/modal/modal";
import { StorageContext } from "../../components/storage/storage-context";
import HttpService from "../../services/http.service";

import { acceptedMediaExtensions } from "../../models/models";

const acceptedMediaTest = (name: string): boolean => !!(acceptedMediaExtensions.audio.filter(accepted => name.toLowerCase().endsWith(accepted)).length);

const AdminTracks: React.FC<any> = (props: any) => {

  const modalContext = React.useContext(ModalContext);

  const [storageContext, setStorageContext] = React.useContext(StorageContext);
  const navigate = useNavigate();

  React.useEffect(() => {
    // Check if the user is authenticated
    // make sure storageContext is resolved first
    Promise.resolve(storageContext).then((storageContextResolved) => {
      if (!storageContextResolved.token) {
        navigate('/login');
      }
    });
  }, []);

  const [trackList, setTrackList] = React.useState<string[]>([]);
  const [trackPlay, setTrackPlay] = React.useState<boolean[]>([]);

  React.useEffect(() => {
    (async () => {
      const res = await HttpService.get<string[]>('tracklist');
      if (res.success) {
        setTrackList(res.body || []);
      } else {
        console.log(res);
      }
    })();
  }, []);

  React.useEffect(() => setTrackPlay(Array(trackList.length).fill(false)), [trackList]);

  return (
    <div className="py-16 px-2 md:px-4 mx-auto diamonds">

      <div className="flex items-center justify-between">
        <h1 className="text-xl gold-text text-center inline-block ml-2 md:ml-8">
          &nbsp;&nbsp;Admin Tracks&nbsp;&nbsp;
        </h1>
        <div className="">
          <button 
            className="btn btn-circle btn-lg ml-auto border border-white mr-2 md:mr-8"
            onClick={() => navigate('/admin')}
          >
            Close
          </button>
        </div>
      </div>

      <div className="text-center mx-2 md:mx-8 px-2 md:px-8 py-8 my-8  bg-gradient-to-br from-pink-200 via-pink-300 to-pink-200 rounded-lg">

        <div 
          className="flex items-center justify-center w-full mb-4"
          onDrop={(e) => {
            e.preventDefault();
            const file = Array.from(e.dataTransfer.files)?.[0];
            if (file && acceptedMediaTest(file.name)) {
              HttpService.upload<string[]>('uploadtrack', file).then(res => {
                (res.success && res.body) && (() => {
                  setTrackList(res.body);
                  modalContext.toast!('success', 'Successfully loaded media filenames.');
                  res.messages.forEach(m => modalContext.toast!('success', m));
                })();
                !(res.success) && (() => {
                  res.messages.forEach(m => modalContext.toast!('warning', m));
                  modalContext.toast!('warning', 'Unable to load media filenames. See console'); 
                  console.log(res);
                })();
              })
            } else {
              modalContext.toast!('error', `${file.name.split('.')?.at(-1)} extension not accepted.`); 
            }
          }}
          onDragOver={(e) => {e.preventDefault()}}
        >
          <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
              </svg>
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Accepted File Extensions:</p>
              <p className="text-xs text-gray-400 dark:text-gray-400">
                {
                  acceptedMediaExtensions.audio.reduce((a,v) => `${a}, ${v}`, ``).substring(2)
                }
              </p>
            </div>

            <input 
              id="dropzone-file"
              type="file"
              accept='audio/*'
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file && acceptedMediaTest(file.name)) {
                  HttpService.upload<string[]>('uploadtrack', file).then(res => {
                    (res.success && res.body) && (() => {
                      setTrackList(res.body);
                      modalContext.toast!('success', 'Successfully loaded media filenames.');
                      res.messages.forEach(m => modalContext.toast!('success', m));
                    })();
                    !(res.success) && (() => {
                      res.messages.forEach(m => modalContext.toast!('warning', m));
                      modalContext.toast!('warning', 'Unable to load media filenames. See console'); 
                      console.log(res);
                    })();
                  })
                } else {
                  modalContext.toast!('error', `${file?.name.split('.')?.at(-1)} extension not accepted.`); 
                }
              }}
            />
          </label>
        </div> 



        {
          trackList.map((t,i) => (
            <div 
              key={i}
              className="relative m-1"
            >

              <Track 
                audioFileName={t} 
                play={trackPlay[i]} 
                onPlayToggle={(p, a) => { setTrackPlay(trackPlay => trackPlay.map((v,ti) => ti === i ? p : false)) }}
              />
              <p 
                className="absolute -right-3 -top-3 bg-red-500 p-1 rounded-full w-10 h-10 cursor-pointer border-2 border-black"
                onClick={() => (modalContext.modal!({prompt: `Are you sure you want to delete\n ${t}?`, options: ["yes", "no"]}))!.then(res => {
                  if (res === "yes") {
                    HttpService.delete<void>('deletetrack', { filename: t }).then(res => {
                      if (res.success) {
                        setTrackList(oldTrackList => oldTrackList.filter(trackListFilename => trackListFilename !== t));
                        res.messages.forEach(m => modalContext.toast!('success', m));
                      } else {
                        modalContext.toast!('warning', `Unable to delete media ${t}`);
                        res.messages.forEach(m => modalContext.toast!('warning', m));
                      }
                    });
                  }
                }).catch(e => {})}
              >
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl">X</span>
              </p>
            </div>
          ))
        }
      </div>
    </div>
  )
}

export default AdminTracks;